// @flow
import * as React from 'react';

import assertions from 'lib/assertions';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import Container from 'components/Container';
import EventTracking from 'lib/EventTracking';
import FormElement from 'components/FormElement';
import Heading from 'components/Heading';
import Input from 'components/Input';
import Link from 'components/Link';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import TextWithGlyph from 'components/TextWithGlyph';
import View from 'components/View';
import WizardFooter from 'components/WizardFooter';

import type { AdminNewAutoMessageOrgs_organizations } from './queries/__generated__/AdminNewAutoMessageOrgs';

export const AUTO_MESSAGE_NAME_CHAR_LIMIT = 35;

type Props = {
  orgs: ?(AdminNewAutoMessageOrgs_organizations[]),
  messageName: ?string,
  orgUuid: ?string,
  viewingOrgUuid: ?string,
  onCreate: (messageName: ?string, orgUuid: ?string, creatorOrgUuid: ?string) => void,
};

type State = {
  messageNameFromField: ?string,
  orgUuidFromField: ?string,
};

class AdminNewAutoMessageCreate extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      messageNameFromField: props.messageName,
      orgUuidFromField: props.orgUuid,
    };
  }

  componentDidMount() {
    EventTracking.trackKey('auto_messaging.create_draft.view');
    if (this.props.orgs && this.props.orgs.length > 0) {
      this.handleOrgChange(this.props.orgs[0].uuid);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.orgs && this.props.orgs && this.props.orgs.length > 0) {
      this.handleOrgChange(this.props.orgs[0].uuid);
    }
  }

  handleOrgChange = (orgUuid: ?string) => {
    this.setState({ orgUuidFromField: orgUuid });
  };

  handleMessageNameChange = (messageName: ?string) => {
    this.setState({ messageNameFromField: messageName });
  };

  render() {
    const { orgs, viewingOrgUuid } = this.props;
    const { messageNameFromField, orgUuidFromField } = this.state;
    const orgOptions = orgs
      ? orgs.map(org => {
          return { label: org.name, value: org.uuid };
        })
      : [];

    const selectedOrg = orgs ? orgs.find(org => org.uuid === orgUuidFromField) : null;

    return (
      <React.Fragment>
        <View>
          <Row size="xxxxxlarge">
            <Container flush size="medium">
              <Text align="left" lineHeight="large">
                <Heading level="1">Create a new auto message draft</Heading>
                <Row size="xxxlarge">
                  <Text element="div">
                    Automatically send messages to students and parents based on data from your SIS
                    or another database. You’ll be walked through the process of exporting reports
                    with recipient data, transfering this data to Remind, composing your message,
                    and activating your auto message.
                  </Text>
                </Row>
                <Row size="xxxlarge">
                  <FormElement
                    showErrorBeforeBlur
                    label="Message name"
                    onValidChange={e => this.handleMessageNameChange(e.target.value)}
                    onInvalidChange={() => this.handleMessageNameChange(undefined)}
                    labelNote="Give this auto message a name that is easily distinguishable. Message names are only
                visible to you and other administrators."
                    validate={e => {
                      assertions.lengthLesserThanOrEqualTo(
                        e.target.value,
                        AUTO_MESSAGE_NAME_CHAR_LIMIT,
                        `Please provide a message name under ${AUTO_MESSAGE_NAME_CHAR_LIMIT} characters long.`
                      );
                    }}
                    element={Input}
                    elementProps={{
                      defaultValue: messageNameFromField,
                      placeholder:
                        '1st Period Attendance, Weekly Lunch Money Balance, Overdue Library Books',
                      name: 'messageName',
                    }}
                  />
                </Row>
                <FormElement
                  label="Organization"
                  validate={value => assertions.notEmpty(value, 'Please select an organization')}
                  onValidChange={value => this.handleOrgChange(value)}
                  onInvalidChange={() => this.handleOrgChange(undefined)}
                  labelNote="Your auto message can only be sent to people in this organization."
                  element={Select}
                  elementProps={{
                    value: orgUuidFromField,
                    placeholder: 'Choose a school or district',
                    name: 'org',
                    options: orgOptions,
                  }}
                />
                {selectedOrg && selectedOrg.studentIdPercentage < 0.5 ? (
                  <TextWithGlyph center={false} glyph="caution" glyphColor="sunrise" size="xsmall">
                    {`${selectedOrg.studentIdPercentage * 100}% of students at ${
                      selectedOrg.name
                    } have student numbers on Remind. You’ll only be able to message students with student numbers. `}
                    <Link href="#">Learn more here</Link>
                  </TextWithGlyph>
                ) : null}
              </Text>
            </Container>
          </Row>
        </View>
        <WizardFooter>
          <Container size="medium" flush>
            <Buttons align="right">
              <Button
                data-test="continue-button"
                disabled={!messageNameFromField || !orgUuidFromField}
                onClick={() =>
                  this.props.onCreate(messageNameFromField, orgUuidFromField, viewingOrgUuid)
                }>
                Create draft
              </Button>
            </Buttons>
          </Container>
        </WizardFooter>
      </React.Fragment>
    );
  }
}

export default AdminNewAutoMessageCreate;
